import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DemoModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/demo-requests`, formData);
      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
        setSuccess(false);
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-[#141414] border border-[#1e1e1e] text-white max-w-md"
        data-testid="demo-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-medium tracking-[-0.5px] text-white">
            Request a Demo
          </DialogTitle>
          <DialogDescription className="text-[#9ca3af] text-sm">
            See how Veranda AI can transform your demand generation.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4" data-testid="demo-success">
            <CheckCircle className="w-16 h-16 text-[#10b981]" />
            <p className="text-lg font-medium">Thank you!</p>
            <p className="text-[#9ca3af] text-center">We'll be in touch soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#9ca3af] text-sm">Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Smith"
                className="bg-[#0a0a0a] border-[#1e1e1e] text-white placeholder-[#6b7280] focus:border-[#3b82f6] focus:ring-[#3b82f6] rounded-[10px]"
                data-testid="demo-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#9ca3af] text-sm">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@company.com"
                className="bg-[#0a0a0a] border-[#1e1e1e] text-white placeholder-[#6b7280] focus:border-[#3b82f6] focus:ring-[#3b82f6] rounded-[10px]"
                data-testid="demo-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-[#9ca3af] text-sm">Company *</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="Acme Building Products"
                className="bg-[#0a0a0a] border-[#1e1e1e] text-white placeholder-[#6b7280] focus:border-[#3b82f6] focus:ring-[#3b82f6] rounded-[10px]"
                data-testid="demo-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#9ca3af] text-sm">Phone (optional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
                className="bg-[#0a0a0a] border-[#1e1e1e] text-white placeholder-[#6b7280] focus:border-[#3b82f6] focus:ring-[#3b82f6] rounded-[10px]"
                data-testid="demo-phone-input"
              />
            </div>

            {error && (
              <p className="text-[#ef4444] text-sm" data-testid="demo-error">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold rounded-[8px] py-2.5 transition-all duration-200 hover:-translate-y-[1px] shadow-glow hover:shadow-glow-hover"
              data-testid="demo-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoModal;
